/**
 * Tiny e-commerce tracking helper.
 *
 * Fires the same event to BOTH Facebook Pixel (`window.fbq`) and Google
 * Tag Manager (`window.dataLayer`) with the conventional event names each
 * platform expects. No-ops silently if the script for that channel hasn't
 * been injected (e.g. admin hasn't set the ID).
 *
 * Conventions:
 *   FB:   fbq('track', '<FbEventName>', { value, currency, content_name, ... })
 *   GTM:  dataLayer.push({ event: '<gtm_event>', ecommerce: { value, currency, items:[...] } })
 *
 * Wrapping fbq calls in try/catch because some ad-blockers stub fbq with
 * something that throws.
 */

const _try = (fn) => {
  try { fn(); } catch (_) { /* ignore — ad-blocker / stub */ }
};

const fb = (name, params) => {
  if (typeof window === "undefined" || !window.fbq) return;
  _try(() => window.fbq("track", name, params || {}));
};

const gtm = (event, payload) => {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  _try(() => {
    window.dataLayer.push({ ecommerce: null }); // clear previous ecommerce object (GA4 best practice)
    window.dataLayer.push({ event, ...(payload || {}) });
  });
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const item = ({ plan_key, plan_name, price, months }) => ({
  item_id: plan_key || "",
  item_name: plan_name || plan_key || "Hosting plan",
  price: num(price),
  quantity: 1,
  item_category: "Hosting",
  item_variant: months ? `${months}m` : undefined,
});

// ---------- Plan card click (AddToCart / ViewContent) ----------
export const trackPlanClick = ({ plan_key, plan_name, price, months = 12 }) => {
  const value = num(price);
  fb("AddToCart", {
    value,
    currency: "INR",
    content_type: "product",
    content_ids: [plan_key],
    content_name: plan_name || plan_key,
  });
  gtm("add_to_cart", {
    ecommerce: {
      currency: "INR",
      value,
      items: [item({ plan_key, plan_name, price, months })],
    },
  });
};

// ---------- Razorpay opens (InitiateCheckout / BeginCheckout) ----------
export const trackBeginCheckout = ({ plan_key, plan_name, price, months = 12, value, currency = "INR" }) => {
  const v = num(value ?? price);
  fb("InitiateCheckout", {
    value: v,
    currency,
    content_type: "product",
    content_ids: [plan_key],
    content_name: plan_name || plan_key,
    num_items: 1,
  });
  gtm("begin_checkout", {
    ecommerce: {
      currency,
      value: v,
      items: [item({ plan_key, plan_name, price: price ?? v, months })],
    },
  });
};

// ---------- Successful payment (Purchase) ----------
export const trackPurchase = ({
  transaction_id, plan_key, plan_name, value, currency = "INR", months = 12,
}) => {
  const v = num(value);
  fb("Purchase", {
    value: v,
    currency,
    content_type: "product",
    content_ids: plan_key ? [plan_key] : undefined,
    content_name: plan_name || plan_key,
    transaction_id,
  });
  gtm("purchase", {
    ecommerce: {
      transaction_id,
      currency,
      value: v,
      items: [item({ plan_key, plan_name, price: v, months })],
    },
  });
};
